import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from '@phosphor-icons/react';
import { api } from '../../lib/api';
import ScoreBadge from '../../components/ScoreBadge';
import { SkeletonAdminDetail } from '../../components/Skeleton';

const MONTH_NAMES = {
  1: 'January', 2: 'February', 3: 'March', 4: 'April',
  5: 'May', 6: 'June', 7: 'July', 8: 'August',
  9: 'September', 10: 'October', 11: 'November', 12: 'December',
};

function EditableCell({ value, field, recordId, clinicianId, onRecordUpdate }) {
  const [editing, setEditing] = useState(false);
  const [displayVal, setDisplayVal] = useState(value);
  const [localValue, setLocalValue] = useState(value ?? '');

  // Keep the displayed value in sync with server truth (e.g. after a recalc
  // reload) so the cell never drifts from what was actually persisted.
  useEffect(() => {
    if (!editing) setDisplayVal(value);
  }, [value, editing]);

  const isZero = displayVal == null || displayVal === 0;
  const isHighlight = field === 'caseload' || field === 'attendance_pct';

  const displayValue = field === 'attendance_pct'
    ? (displayVal != null ? (displayVal * 100).toFixed(2) + '%' : '-')
    : (displayVal != null && displayVal !== '' ? displayVal : '-');

  const handleBlur = async () => {
    setEditing(false);
    let parsed;
    if (field === 'attendance_pct') {
      parsed = parseFloat(localValue) / 100;
      if (isNaN(parsed)) return;
    } else {
      parsed = parseFloat(localValue);
      if (isNaN(parsed)) return;
    }
    if (parsed === displayVal) return;
    setDisplayVal(parsed);
    try {
      const updated = await api.admin.updateClinicianRecord(clinicianId, recordId, { [field]: parsed });
      if (onRecordUpdate) onRecordUpdate(recordId, updated);
    } catch (err) {
      console.error('Save failed:', err);
      setDisplayVal(value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') e.target.blur();
    if (e.key === 'Escape') { setEditing(false); setLocalValue(value ?? ''); }
  };

  return (
    <div className="inline-block min-w-[60px]">
      {editing ? (
        <input
          type="number"
          step={field === 'attendance_pct' ? '0.01' : '1'}
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          autoFocus
          className="w-full border border-primary rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          style={{ MozAppearance: 'textfield' }}
        />
      ) : (
        <span
          className={`cursor-pointer hover:bg-primary-10 px-2 py-1 rounded transition-colors text-sm block ${
            isHighlight && isZero ? 'text-destructive' : ''
          }`}
          onClick={() => {
            setLocalValue(field === 'attendance_pct' && value != null ? (value * 100).toFixed(2) : (value ?? ''));
            setEditing(true);
          }}
        >
          {displayValue}
        </span>
      )}
    </div>
  );
}

export default function AdminClinicianDetail() {
  const { clinicianId } = useParams();
  const navigate = useNavigate();
  const [clinician, setClinician] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fullName, setFullName] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmToggleOpen, setConfirmToggleOpen] = useState(false);

  const loadClinician = () => {
    api.admin.clinician(clinicianId)
      .then((data) => {
        setClinician(data);
        setFullName(data.full_name || '');
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadClinician();
  }, [clinicianId]);

  const handleSaveName = async () => {
    if (!fullName.trim() || fullName === clinician.full_name) return;
    setSaving(true);
    try {
      const updated = await api.admin.updateClinician(clinicianId, { full_name: fullName.trim() });
      setClinician(prev => ({ ...prev, ...updated }));
    } catch (err) {
      console.error('Save name failed:', err);
    }
    setSaving(false);
  };

  const handleToggleActive = async () => {
    setConfirmToggleOpen(false);
    setSaving(true);
    try {
      const updated = await api.admin.updateClinician(clinicianId, { active: !clinician.active });
      setClinician(prev => ({ ...prev, ...updated }));
    } catch (err) {
      console.error('Toggle active failed:', err);
    }
    setSaving(false);
  };

  const handleRecordUpdate = (recordId, updatedRecord) => {
    setClinician(prev => ({
      ...prev,
      records: prev.records.map(r => r.id === recordId ? updatedRecord : r),
    }));
  };

  if (loading) return <SkeletonAdminDetail />;
  if (!clinician) return <p className="text-sm text-gray-500">Clinician not found.</p>;

  return (
    <div>
      {/* Back link */}
      <button
        onClick={() => navigate('/admin/clinicians')}
        className="flex items-center gap-1.5 text-sm text-primary font-medium mb-4 hover:underline"
      >
        <ArrowLeft size={16} /> Back
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-[32px] font-semibold text-text">{clinician.full_name}</h1>
          {clinician.clinics && (
            <p className="text-sm text-gray-500 mt-1">{clinician.clinics.name}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs font-medium px-3 py-1.5 rounded-full ${
            clinician.active
              ? 'bg-green-50 text-green-700'
              : 'bg-red-50 text-destructive'
          }`}>
            {clinician.active ? 'Active' : 'Inactive'}
          </span>
          <button
            onClick={() => setConfirmToggleOpen(true)}
            disabled={saving}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${
              clinician.active
                ? 'text-destructive border border-destructive-30 hover:bg-destructive-10'
                : 'text-white bg-primary hover:bg-primary-80'
            }`}
          >
            {clinician.active ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </div>

      {/* Edit Name */}
      <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6 mb-6">
        <p className="text-sm font-bold text-text mb-3">Clinician Name</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            className="flex-1 border border-[#E6E6E6] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30"
          />
          <button
            onClick={handleSaveName}
            disabled={saving || !fullName.trim() || fullName === clinician.full_name}
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-80 transition-colors disabled:opacity-50 flex-shrink-0"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Data Records Table */}
      <div className="bg-white rounded-xl border border-[#E6E6E6] p-4 md:p-6">
        <h2 className="text-lg font-bold text-text mb-4">Data Records</h2>

        {clinician.records.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">No data records for this clinician</p>
        ) : (
          <>
            {/* Desktop table */}
            <div className="overflow-x-auto">
              <table className="w-full table-fixed hidden md:table">
                <colgroup>
                  <col className="w-[18%]" />
                  <col className="w-[12%]" />
                  <col className="w-[15%]" />
                  <col className="w-[15%]" />
                  <col className="w-[18%]" />
                  <col className="w-[15%]" />
                </colgroup>
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 text-sm font-semibold text-gray-700">Month</th>
                    <th className="text-left py-3 text-sm font-semibold text-gray-700">CScore</th>
                    <th className="text-left py-3 text-sm font-semibold text-gray-700">Caseload</th>
                    <th className="text-left py-3 text-sm font-semibold text-gray-700">Discharges</th>
                    <th className="text-left py-3 text-sm font-semibold text-gray-700">Length of Stay</th>
                    <th className="text-left py-3 text-sm font-semibold text-gray-700">Attendance</th>
                  </tr>
                </thead>
                <tbody>
                  {clinician.records.map((record) => (
                    <tr key={record.id} className="border-b border-gray-50 last:border-0">
                      <td className="py-3.5 text-sm text-text">
                        {MONTH_NAMES[record.month]} {record.year}
                      </td>
                      <td className="py-3.5"><ScoreBadge score={record.cscore || 0} /></td>
                      <td className="py-3.5">
                        <EditableCell value={record.caseload} field="caseload" recordId={record.id} clinicianId={clinicianId} onRecordUpdate={handleRecordUpdate} />
                      </td>
                      <td className="py-3.5">
                        <EditableCell value={record.discharges} field="discharges" recordId={record.id} clinicianId={clinicianId} onRecordUpdate={handleRecordUpdate} />
                      </td>
                      <td className="py-3.5">
                        <EditableCell value={record.length_of_stay} field="length_of_stay" recordId={record.id} clinicianId={clinicianId} onRecordUpdate={handleRecordUpdate} />
                      </td>
                      <td className="py-3.5">
                        <EditableCell value={record.attendance_pct} field="attendance_pct" recordId={record.id} clinicianId={clinicianId} onRecordUpdate={handleRecordUpdate} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {clinician.records.map((record) => (
                <div key={record.id} className="border border-gray-100 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-text">
                      {MONTH_NAMES[record.month]} {record.year}
                    </span>
                    <ScoreBadge score={record.cscore || 0} />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                    <div>Caseload: <EditableCell value={record.caseload} field="caseload" recordId={record.id} clinicianId={clinicianId} onRecordUpdate={handleRecordUpdate} /></div>
                    <div>Discharges: <EditableCell value={record.discharges} field="discharges" recordId={record.id} clinicianId={clinicianId} onRecordUpdate={handleRecordUpdate} /></div>
                    <div>LOS: <EditableCell value={record.length_of_stay} field="length_of_stay" recordId={record.id} clinicianId={clinicianId} onRecordUpdate={handleRecordUpdate} /></div>
                    <div>Attendance: <EditableCell value={record.attendance_pct} field="attendance_pct" recordId={record.id} clinicianId={clinicianId} onRecordUpdate={handleRecordUpdate} /></div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Confirm activate/deactivate modal */}
      {confirmToggleOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4" onClick={() => setConfirmToggleOpen(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-text mb-2">
              {clinician.active ? 'Deactivate' : 'Activate'} Clinician
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to {clinician.active ? 'deactivate' : 'activate'} <strong>{clinician.full_name}</strong>?
              {clinician.active && ' They will no longer appear in clinic views.'}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setConfirmToggleOpen(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleToggleActive}
                className={`px-4 py-2 text-sm font-medium rounded-lg ${
                  clinician.active
                    ? 'text-white bg-destructive hover:bg-red-700'
                    : 'text-white bg-primary hover:bg-primary-80'
                }`}
              >
                {clinician.active ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
